import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'reactstrap';
import * as turf from '@turf/turf';
import { featureCollection } from '@turf/turf';
import { useEffectOnce } from 'react-use';

import MapContainer from '../map/MapContainer';
import NaipLayer from '../map/NaipLayer';
import useInitializeMapEffect from '../../hooks/useInitializeMapEffect';
import {
  addReferenceLayers,
  forceMapResize,
  getSourceData,
  setSourceData,
} from '../../utils/map';
import { captureEvent } from '../../utils/analytics';
import LandBoundariesLayer from './components/LandBoundariesLayer';
import AddressSearch from './components/AddressSearch';
import Logo from '../icons/Logo';
import ChevronIcon from '../icons/ChevronIcon';
import {
  addFallbackBoundary,
  getNonFallbackFeatures,
  layerForTileset,
  sortTilesetSuitability,
} from './helpers';
import {
  useDisablePageZoom,
  useDrawMode,
  useSelectedFeatures,
  useSuitabilityPerCrop,
  useTilesetsData,
  useUpdateTilesetVisibility,
} from './hooks';
import {
  DemoMapBackground,
  FullOverlay,
  InfoText,
  MainContainer,
  PromptCard,
  ResponsiveButton,
} from './components/basic';
import {
  DRAWN_FEATURE_ID_PREFIX,
  INIT_MAP_POSITION,
  MAIN_SOURCE_ID,
  STEPS,
} from './constants';
import InfoPanel from './components/InfoPanel';
import PostSubmit from './components/PostSubmit';
import { emptyFeature } from '../../utils/geojson';
import useAsSequentialAsync from '../../hooks/useAsSequentialAsync';
import PositionedOverMap from '../basics/PositionedOverMap';
import useAddClippableTilesetLayers from '../../hooks/useAddClippableTilesetLayers';

const PublicSuitabilityTool = () => {
  // No zoom on mobile:
  useDisablePageZoom();

  // Map state
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const loadedMap = mapLoaded && mapRef.current;

  // Initialize the map
  useInitializeMapEffect({
    mapContainer,
    mapRef,
    mapLoaded,
    setMapLoaded,
    onLoad: () => {
      addReferenceLayers(mapRef.current);
    },
    ...INIT_MAP_POSITION,
    controls: {
      geolocation: false,
      compass: false,
      fullscreen: false,
    },
  });

  // Track page state (step)
  const [step, setStep] = useState(STEPS.INPUT_ADDRESS);

  // Analytics
  useEffectOnce(() => {
    captureEvent('public-suitability-tool-started');
  });
  useEffect(() => {
    captureEvent('public-suitability-tool-step', { step });
  }, [step]);

  // Fix issue where canvas doesn't resize properly
  useEffect(() => {
    forceMapResize();
  }, [step]);

  // Load tilesets & add to map
  const tilesets = useTilesetsData();
  useAddClippableTilesetLayers(loadedMap, tilesets);

  // State for selected tileset
  const [selectedTilesetId, setSelectedTilesetId] = useState(null);
  const selectTileset = useCallback(
    (name) => {
      const id = tilesets.find((tileset) => tileset.name === name)?.id;
      setSelectedTilesetId(id ?? null);
      captureEvent('public-suitability-tool-tileset-selected', { name });
    },
    [tilesets],
  );

  // State for selected parcels
  const {
    toggleFeatureId,
    resetSelectedFeatures,
    selectedFeatureIds,
    selectedFeatures,
    updateSelectedFeatures,
    setSelectedFeatureIds,
  } = useSelectedFeatures(mapRef);

  // This function sets the data for the main source and updates the selected
  // features. To avoid race conditions, we use `asSequentialAsync`, which
  // ensures that although the function may be called multiple times in quick
  // succession, each call will be processed one at a time, in order.
  //
  // `transformData` should accept a FeatureCollection and return a FeatureCollection.
  const setLayerData = useAsSequentialAsync(async (transformData) => {
    const map = mapRef.current;
    if (!map) return;

    // Get existing data and transform it with `transformData`
    const existing = getSourceData(map, MAIN_SOURCE_ID);
    const data = transformData(turf.featureCollection(existing));

    // Set the transformed data
    await setSourceData(map, MAIN_SOURCE_ID, data);

    // Select all drawn features
    const newFeatureIds = data.features
      .map(({ properties: { id } }) => id)
      .filter((id) => id.startsWith(DRAWN_FEATURE_ID_PREFIX));
    setSelectedFeatureIds(newFeatureIds);
  });

  // DRAW MODE
  const {
    isDrawMode,
    setIsDrawMode,
    featureIsSelected: drawModeFeatureIsSelected,
    isDrawing,
    deleteFeature: deleteSelectedDrawnFeature,
    deleteAll: deleteAllDrawnFeatures,
    drawPolygon,
  } = useDrawMode({
    map: loadedMap,
    setLayerData,
  });

  const infoPanelResetHandler = () => {
    if (isDrawMode) deleteAllDrawnFeatures();
    else resetSelectedFeatures();
  };

  // When selectedTilesetId changes, update the visibility of the tileset layers
  const shouldShowTileset = selectedFeatureIds.length > 0;
  useUpdateTilesetVisibility(
    loadedMap,
    tilesets,
    selectedTilesetId,
    shouldShowTileset,
  );

  // When selectedFeatures changes, update the raster layers' masks
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Update the layers with their updateMask method
    tilesets.forEach(({ id }) => {
      const layer = layerForTileset(map, id);
      if (layer) layer.updateMask(selectedFeatures);
    });
  }, [selectedFeatures, tilesets]);

  // Calculate suitability per crop
  const suitabilityPerCrop = useSuitabilityPerCrop(
    loadedMap,
    tilesets,
    selectedFeatures,
  );

  // When suitability per crop changes, if no tileset selected, select the most suitable crop
  useEffect(() => {
    if (selectedTilesetId) return;

    const crops = Object.values(suitabilityPerCrop);
    if (crops.length === 0) return;

    const [max] = sortTilesetSuitability(crops);
    if (!max) return;

    const { name: mostSuitableCrop, suitableAcres: acresForCrop } = max;
    if (acresForCrop) selectTileset(mostSuitableCrop);
  }, [selectedTilesetId, suitabilityPerCrop, selectTileset]);

  const enterDrawMode = async () => {
    // Remove existing features
    const data = emptyFeature();
    await setSourceData(loadedMap, MAIN_SOURCE_ID, data);

    resetSelectedFeatures();

    setIsDrawMode(true);
    setStep(STEPS.SELECT_REGIONS);
  };

  if (step === STEPS.POST_SUBMIT) {
    // NOTE: this approach means that the post-submit step removes the map, so
    // the "Back" button in <PostSubmit> has to reload the page to get it back.
    // Not ideal but it works for now (better solution would be a full-page overlay).
    return <PostSubmit />;
  }

  return (
    <MainContainer>
      <div className="h-100 w-100 position-relative">
        <MapContainer
          ref={mapContainer}
          className="map-container h-100 w-100"
          style={{
            // Hide the map for the initial step:
            visibility: step === STEPS.INPUT_ADDRESS ? 'hidden' : 'visible',
          }}
        />
        {/* Show demo map background for the initial step: */}
        {step === STEPS.INPUT_ADDRESS && <DemoMapBackground />}

        {step === STEPS.INPUT_ADDRESS && (
          <FullOverlay>
            <div className="d-flex flex-column pb-5">
              <InfoText>
                <div>Explore crop suitability for your land</div>
                <div style={{ fontSize: '70%' }} className="mt-2">
                  Enter your address or coordinates
                </div>
              </InfoText>
              <AddressSearch
                className="mx-auto"
                submit={async ({ address, center }) => {
                  const map = mapRef.current;
                  if (!map) return;

                  captureEvent('public-suitability-tool-address-submit', {
                    address,
                    center,
                  });

                  map.setCenter(center);
                  await addFallbackBoundary(
                    mapRef,
                    toggleFeatureId,
                    resetSelectedFeatures,
                  );
                  setStep(STEPS.SELECT_REGIONS);
                }}
                conusOnly
              />
            </div>
          </FullOverlay>
        )}

        {step === STEPS.DRAW_MODE_PROMPT && (
          <FullOverlay>
            <PromptCard>
              <div className="mb-2">Draw parcels?</div>

              <div className="font-small">
                <div className="mb-3">
                  This will remove existing land parcels on the map
                </div>
                <div className="mt-2 d-flex justify-content-center">
                  <ResponsiveButton
                    color="outline-primary"
                    sizes={['sm', 'xs']}
                    className="mr-2"
                    onClick={enterDrawMode}
                  >
                    Draw parcels
                  </ResponsiveButton>

                  <ResponsiveButton
                    color="outline-primary"
                    sizes={['sm', 'xs']}
                    onClick={async () => {
                      setStep(STEPS.SELECT_REGIONS);
                    }}
                  >
                    Cancel
                  </ResponsiveButton>
                </div>
              </div>
            </PromptCard>
          </FullOverlay>
        )}

        {step === STEPS.INPUT_ADDRESS && (
          <PositionedOverMap placement="top left">
            <Logo width={30} height={30} />
          </PositionedOverMap>
        )}

        {step === STEPS.SELECT_REGIONS && (
          <PositionedOverMap placement="top left">
            <Button
              size="sm"
              className="map-button py-2"
              onClick={() => {
                // reset draw mode
                setIsDrawMode(false);

                // reset crop/tileset selection
                setSelectedTilesetId(null);

                // reset features
                loadedMap
                  .getSource(MAIN_SOURCE_ID)
                  .setData(featureCollection(emptyFeature()));

                // reset feature selection
                resetSelectedFeatures();

                // reset step
                setStep(STEPS.INPUT_ADDRESS);
              }}
            >
              <ChevronIcon direction="left" height={13} width={13} />
            </Button>

            {!isDrawMode && (
              <Button
                size="sm"
                className="ml-2 map-button"
                onClick={async () => {
                  const features = getNonFallbackFeatures(loadedMap);
                  if (features.length > 0) {
                    setStep(STEPS.DRAW_MODE_PROMPT);
                  } else {
                    await enterDrawMode();
                  }
                }}
              >
                Draw parcels
              </Button>
            )}
          </PositionedOverMap>
        )}

        {isDrawMode && (
          <PositionedOverMap placement="top right">
            {drawModeFeatureIsSelected && (
              <ResponsiveButton
                sizes={['xs', 'sm']}
                className="map-button mr-2"
                onClick={async () => {
                  await deleteSelectedDrawnFeature();

                  updateSelectedFeatures();
                }}
              >
                Delete parcel
              </ResponsiveButton>
            )}

            <ResponsiveButton
              sizes={['xs', 'sm']}
              className="map-button"
              onClick={drawPolygon}
              disabled={isDrawing}
            >
              Create parcel
            </ResponsiveButton>
          </PositionedOverMap>
        )}

        {step === STEPS.SELECT_REGIONS && (
          <InfoPanel
            loadedMap={loadedMap}
            tilesets={tilesets}
            suitabilityPerCrop={suitabilityPerCrop}
            selectedTilesetId={selectedTilesetId}
            selectTileset={selectTileset}
            selectedFeatures={selectedFeatures}
            reset={infoPanelResetHandler}
            isDrawMode={isDrawMode}
          />
        )}
      </div>

      {/* These components don't render anything: */}
      {mapLoaded && <NaipLayer map={mapRef.current} />}
      {mapLoaded && (
        <LandBoundariesLayer
          mapRef={mapRef}
          selectedFeatureIds={selectedFeatureIds}
          toggleFeatureId={toggleFeatureId}
          isDrawMode={isDrawMode}
        />
      )}
    </MainContainer>
  );
};

export default PublicSuitabilityTool;
